import Vue from 'vue'
import axios from 'axios'
import NotFoundError from './NotFoundError'
import moduleList from '@/global/moduleList'
import { has } from 'lodash/object'
import { notify as notifyBugsnag } from '@/global/plugins/error-reporting'
import { serialize } from 'object-to-formdata'
import { isEmpty } from 'lodash'
import { notify as notifySnackbar } from '../notification/notificationBus'

/**
 * @class
 * @constructor
 * @public
 */
class Api {
  constructor (module = '') {
    this.basePath = '/api'
    this.module = module

    return new Proxy(this, {
      get: function (api, field) {
        if (field in api) return api[field]

        if (moduleList.indexOf(field) !== -1 && api.module === '') return new Api(field)

        return api
      }
    })
  }

  async get (path, params, config) {
    return await this._request('GET', path, params, config)
  }

  async put (path, params, config) {
    return await this._request('PUT', path, params, config)
  }

  async post (path, params, config = {}) {
    return await this._request('POST', path, params, config)
  }

  async patch (path, params) {
    return await this._request('PATCH', path, params)
  }

  async delete (path, params) {
    return await this._request('DELETE', path, params)
  }

  async _request (method, path, data, axiosConfig = {}) {
    let response = null

    // Pull our own (non-axios) notification config out of the request options so
    // it never leaks into the actual HTTP request. It is kept for the duration of
    // this request and consumed by every _pushNotification call below (both the
    // success message and any error handler message), so the whole response of a
    // marked request is presented the same way. Requests that don't set it keep
    // the default snackbar behaviour.
    const { notification = null, reloadOnUnauthorized = true, ...restAxiosConfig } = axiosConfig || {}
    this._notificationConfig = notification
    this._reloadOnUnauthorized = reloadOnUnauthorized !== false

    const config = this._prepareConfig(method, path, data, restAxiosConfig)

    try {
      const { data: fetchedData } = await axios.request(config)
      response = fetchedData

      const { message = '' } = response
      this._pushNotification(message)
    }
    catch (e) {
      const extendedError = this._extendError(e)

      this._handleError(extendedError)

      throw extendedError
    }

    return response
  }

  _prepareConfig (requestMethod, path, formData, axiosConfig = {}) {
    path = (path || '').trim().replace(/^\/|\/$/g, '')
    const paths = ([this.module, path]).join('/')
    const { data, method } = this._prepareData(formData, requestMethod)

    const config = {
      ...axiosConfig,
      method,
      url: this.basePath + '/' + paths,
      maxRedirects: 0,
      withCredentials: true
    }

    if (method === 'GET') {
      // JSON-stringify nested objects
      const jsonParams = {}
      for (const key in data) {
        if (data[key] && !Array.isArray(data[key]) && typeof data[key] === 'object') {
          jsonParams[key] = JSON.stringify(data[key])
        }
        else {
          jsonParams[key] = data[key]
        }
      }

      config.params = jsonParams
    }
    else {
      config.data = data
    }

    return config
  }

  _prepareData (data, method) {
    if (data && Object.values(data).some((item) => item instanceof File)) {
      if (['PUT', 'PATCH'].includes(method)) {
        data._method = method
        method = 'POST'
      }
      return { data: serialize(data, { indices: true, allowEmptyArrays: false }), method }
    }
    return { data, method }
  }

  _extendError (error) {
    const status = error.response?.status
    const isCancelled = error.code === 'ERR_CANCELED'
    const isNetworkCode = error.code === 'ERR_NETWORK'

    // Error classes
    error.isClientError = status && status >= 400 && status <= 499
    error.isServerError = status && status >= 500
    error.isNetworkError = isNetworkCode || (has(error, 'request') && !has(error, 'response'))
    error.isApplicationError = !isCancelled && !has(error, 'request') && !has(error, 'response')

    // Client errors (4xx)
    error.isUnauthorizedError = error.isClientError && status === 401
    error.isForbiddenError = error.isClientError && status === 403
    error.isValidationError = error.isClientError && status === 422
    error.isTooManyRequestsError = error.isClientError && status === 429
    error.isClientNotFoundError = error.isClientError && status === 404
    error.isMethodNotAllowedError = error.isClientError && status === 405
    error.isRequestEntityTooLargeError = error.isClientError && status === 413

    // Server errors (5xx)
    error.isMaintenanceMode = error.isServerError && status === 503

    error.getValidationErrors = () => !error.isValidationError ? {} : error.response.data.errors

    return error
  }

  _handleError (error) {
    switch (true) {
      case error.isForbiddenError:
        this._handleForbiddenError(error)
        break

      case error.isUnauthorizedError:
        this._handleUnauthorizedError(error)
        break

      case error.isApplicationError:
        this._handleApplicationError()
        break

      case error.isValidationError:
        this._handleValidationError(error)
        break

      case error.isClientNotFoundError:
        this._handleClientNotFoundError(error)
        break

      case error.isMethodNotAllowedError:
        this._handleMethodNotAllowedError(error)
        break

      case error.isRequestEntityTooLargeError:
        this._handleRequestEntityTooLargeError(error)
        break

      case error.isMaintenanceMode:
        this._handleMaintenanceMode()
        break

      case error.isNetworkError:
        this._handleNetworkError()
        break

      case error.isTooManyRequestsError:
        this._handleTooManyRequestsError(error)
        break

      case error.isServerError:
        this._handleServerError(error)
        break

      case error.isClientError:
        this._handleClientError(error)
        break

      default:
        this._handleUnknownError(error)
    }
  }

  /**
   * `notification: { silent: true }` keeps a request's outcome off the screen -
   * both the success message and whatever an error handler would have said. For the
   * calls a user never asked for and can do nothing about: a screen remembering its
   * own layout, say, which has no business interrupting whatever is being read.
   * What the handlers otherwise do is untouched - Bugsnag is still told. The reload
   * a 401 sets off is turned off separately, with `reloadOnUnauthorized`.
   */
  _pushNotification (message) {
    const { silent, ...notificationConfig } = this._notificationConfig || {}

    if (silent) {
      return
    }

    if (message && message.trim() !== '') {
      notifySnackbar(message, notificationConfig)
    }
  }

  /**
   * Appends the HTTP status code to an error message (eg. "Server error (HTTP 500)")
   * so users can report the exact failure. Errors without a response (network,
   * application, cancelled) have no status and are returned unchanged.
   */
  _withStatusCode (message, error) {
    const status = error?.response?.status

    if (!message || !status) {
      return message
    }

    return `${message} (HTTP ${status})`
  }

  /**
   * A 401 normally means the session is gone, and the reload is what sends the user
   * back to the login screen.
   *
   * `reloadOnUnauthorized: false` leaves them where they are. For the calls that go
   * out on their own, without anybody asking - a screen saving its own layout, say:
   * one of those answering 401 is no reason to throw away what the user was in the
   * middle of. Whatever they do next goes through a request that does reload.
   */
  _handleUnauthorizedError (error) {
    this._pushNotification(this._withStatusCode(Vue.prototype.$t('base.unauthorized_error'), error))

    if (!this._reloadOnUnauthorized) {
      return
    }

    setTimeout(() => {
      window.location.reload()
    }, 500)
  }

  _handleUnknownError (error) {
    this._pushNotification(this._withStatusCode(Vue.prototype.$t('base.unexpected_error') || 'Something went wrong', error))
    notifyBugsnag(error)
  }

  _handleForbiddenError (error) {
    this._pushNotification(this._withStatusCode(Vue.prototype.$t('base.insufficient_rights'), error))
  }

  _handleClientNotFoundError (error) {
    notifyBugsnag(new NotFoundError('Got 404 from server!'))
    this._pushNotification(this._withStatusCode(Vue.prototype.$t('base.page_not_found_title'), error))
  }

  _handleMethodNotAllowedError (error) {
    this._pushNotification(this._withStatusCode(Vue.prototype.$t('base.method_not_allowed_error'), error))
  }

  _handleRequestEntityTooLargeError (error) {
    this._pushNotification(this._withStatusCode(Vue.prototype.$t('base.request_entity_too_large_error'), error))
  }

  _handleApplicationError () {
    this._pushNotification(Vue.prototype.$t('base.application_error'))
  }

  _handleClientError (error) {
    this._pushNotification(this._withStatusCode(error?.response?.data?.message || error?.message, error))
  }

  _handleNetworkError () {
    this._pushNotification(Vue.prototype.$t('base.network_error'))
  }

  _handleMaintenanceMode () {
    localStorage.setItem('LAST_URL', window.location)
    window.location = '/maintenance-mode'
  }

  _handleTooManyRequestsError (error) {
    this._pushNotification(this._withStatusCode(Vue.prototype.$t('base.too_many_requests_error'), error))
  }

  _handleServerError (error) {
    this._pushNotification(this._withStatusCode(Vue.prototype.$t('base.server_error'), error))

    if (error.response.status === 404) {
      notifyBugsnag(new NotFoundError('Got 404 from server!'))
    }
  }

  _handleValidationError (error) {
    const validationErrors = error.getValidationErrors()
    let message = error?.response?.data?.message ? error?.response?.data?.message : Vue.prototype.$t('base.validation_error')
    if (validationErrors && typeof validationErrors === 'object' && !Array.isArray(validationErrors) && !isEmpty(validationErrors)) {
      message = Object.entries(validationErrors).map(([, errors]) => {
        if (Array.isArray(errors)) {
          return errors.join(', ')
        }
        else {
          return errors
        }
      }).join('<br>')
    }
    this._pushNotification(message)
  }
}

export function api (module) {
  return module ? new Api(module) : new Api()
}
